import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, fmtMoney } from "@/lib/api";
import { chargeTarget, STATUS } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const OUTCOMES = ["charged", "partial", "declined", "failed"];

export function ProcessChargeDialog({ booking, open, onOpenChange, onDone }) {
  const [merchants, setMerchants] = useState([]);
  const [v, setV] = useState({ status: "charged", processed_merchant_id: "", charged_amount: "", txn_ref: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/merchants").then((r) => setMerchants(r.data)); }, []);
  useEffect(() => {
    if (open) setV({ status: "charged", processed_merchant_id: booking.target_merchant_id || "", charged_amount: String(chargeTarget(booking)), txn_ref: "", notes: "" });
  }, [open, booking]);
  const needsTxn = v.status === "charged" || v.status === "partial";
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/process`, {
        status: v.status, processed_merchant_id: v.processed_merchant_id || null,
        charged_amount: needsTxn ? Number(v.charged_amount) : null, txn_ref: needsTxn ? v.txn_ref : null, notes: v.notes,
      });
      toast.success(`${booking.booking_ref} marked ${STATUS[v.status].label}`);
      onOpenChange(false); onDone?.();
    } catch (ex) { toast.error(apiError(ex, "Processing failed")); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-lg" data-testid="process-charge-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Record charge outcome</DialogTitle>
          <DialogDescription className="text-slate-400">Expected agency charge: <span className="font-mono text-emerald-400 font-bold">{fmtMoney(chargeTarget(booking))}</span> · Agent requested <span className="text-slate-200">{booking.target_merchant_name}</span></DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {OUTCOMES.map((s) => (
              <button type="button" key={s} data-testid={`process-status-${s}`} onClick={() => setV({ ...v, status: s })}
                className={`rounded-md border px-2 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${v.status === s ? STATUS[s].cls + " ring-1 ring-current" : "border-slate-700 text-slate-500 hover:text-slate-200"}`}>{STATUS[s].label}</button>
            ))}
          </div>
          <label className="block text-sm"><span className="text-slate-400">Actual merchant used {needsTxn && "*"}</span>
            <select data-testid="process-merchant" className="field mt-1" required={needsTxn} value={v.processed_merchant_id} onChange={set("processed_merchant_id")}>
              <option value="">Select…</option>{merchants.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select></label>
          {needsTxn && (
            <div className="grid grid-cols-2 gap-4">
              <label className="block text-sm"><span className="text-slate-400">Charged amount (USD) *</span><input data-testid="process-amount" type="number" step="0.01" min="0.01" required className="field mt-1 font-mono" value={v.charged_amount} onChange={set("charged_amount")} /></label>
              <label className="block text-sm"><span className="text-slate-400">Gateway txn ID / ref *</span><input data-testid="process-txn" required className="field mt-1 font-mono" value={v.txn_ref} onChange={set("txn_ref")} /></label>
            </div>
          )}
          <label className="block text-sm"><span className="text-slate-400">Processor notes *</span><textarea data-testid="process-notes" required rows={3} className="field-area mt-1" value={v.notes} onChange={set("notes")} placeholder={needsTxn ? "Approval code, terminal, remarks…" : "Decline reason / error returned by gateway…"} /></label>
          <Button data-testid="process-submit-btn" type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-500">{busy ? "Saving…" : `Submit as ${STATUS[v.status].label}`}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
